import _ from 'lodash/fp';
import { CHAT_MESSAGE_TYPE, SESSION_TYPES } from '@medflex/utils/constants';
import Logger from '@medflex/utils/Logger';
import makeApiRequest from '../../../api/makeApiRequest';

export const TYPE = 'sessionChats';
let retryCount = 0;

const initialChatState = {
  participant: null,
  chat: null,
  status: {
    loadChat: 'idle',
    loadMessages: 'idle',
  },
  messages: [],
  errors: [],
};

/*
 * If we have a session route will be /sessions/${id}/${participantId}/messages?tan=${tan}
 * If we have a contact route will be /sessions/${id}/messages
 */
const calculateEndpointRoute = (id, participantId, tan) =>
  participantId && tan
    ? `/sessions/${id}/${participantId}/messages?tan=${tan}`
    : `/sessions/${id}/messages`;

const updateStateChat = (state, chats, userId) => {
  const availableSessionIds = Object.keys(state);
  if (!availableSessionIds.length) return state;

  const newState = _.cloneDeep(state);

  for (const chat of chats) {
    if (userId && !chat.participants.find(p => p.userId && p.userId === userId)) {
      return delete newState[chat.id];
    }

    if (!newState[chat.id]) {
      // if chat is not in state
      newState[chat.id] = chat;
      // eslint-disable-next-line no-continue
      continue;
    }

    newState[chat.id].chat = _.mergeAll({}, newState[chat.id].chat, chat);
  }

  return newState;
};

const SessionChats = {
  name: TYPE,
  state: {},
  reducers: {
    onSuccess(state, { id, type }) {
      const session = state[id];
      return {
        ...state,
        [id]: {
          ...session,
          status: {
            ...session?.status,
            [type]: 'succeeded',
          },
          errors: session?.errors?.filter(e => e.type !== type) || null,
        },
      };
    },
    onError(state, { id, type, error }) {
      const session = state[id];
      return {
        ...state,
        [id]: {
          ...session,
          status: {
            ...session?.status,
            [type]: 'failed',
          },
          errors: [...(session?.errors || []), { type, error }],
        },
      };
    },
    onLoading(state, { id, type }) {
      const session = state[id];
      return {
        ...state,
        [id]: {
          ...initialChatState,
          ...session,
          status: {
            ...initialChatState.status,
            ...session?.status,
            [type]: 'loading',
          },
          errors: initialChatState.errors,
        },
      };
    },
    onMessagesLoaded(state, payload) {
      const { id, messages } = payload;
      const session = state[id];
      return {
        ...state,
        [id]: {
          ...session,
          messages,
        },
      };
    },
    onChatLoaded(state, chat, superUserId) {
      const { id, attributes } = chat;
      const session = state[id];
      const participant = attributes.participants.find(p => p.userId === superUserId);

      return {
        ...state,
        [id]: {
          ...session,
          participant,
          chat: {
            ...attributes,
            id,
          },
        },
      };
    },
    onMessageCreated(state, payload) {
      const { sessionId, id, ...attributes } = payload;
      const session = state[sessionId];

      if (!session || !session.participant) {
        return state;
      }

      const { participantId } = session.participant;
      const isSystemMessage = attributes.type === CHAT_MESSAGE_TYPE.SYSTEM;
      return {
        ...state,
        [sessionId]: {
          ...session,
          messages: [
            ...session.messages,
            { id, attributes, type: CHAT_MESSAGE_TYPE.SESSION_MESSAGES },
          ],
          ...((participantId === attributes.sender || isSystemMessage) && {
            chat: {
              ...session.chat,
              messageLastReadId: id,
            },
          }),
          // update lastSeenMessageTimestamp of sender (useful in case a user writes a
          // message before markAsRead has been triggered)
          ...(participantId !== attributes.sender &&
            !isSystemMessage && {
              chat: {
                ...session.chat,
                participants: session.chat.participants.map(p => {
                  if (p.participantId === attributes.sender) {
                    return { ...p, lastSeenMessageTimestamp: attributes.time };
                  }
                  return p;
                }),
              },
            }),
        },
      };
    },
    onMulticastMessageCreated(state, payload) {
      const chatMessage = payload.find(({ sessionId }) => state[sessionId]);
      if (!chatMessage) {
        return state;
      }
      const session = state[chatMessage.sessionId];
      const participants = session.chat.participants.map(p => {
        if (p.participantId === chatMessage.sender) {
          return { ...p, lastSeenMessageTimestamp: chatMessage.time };
        }
        return p;
      });

      return {
        ...state,
        [chatMessage.sessionId]: {
          ...session,
          messages: [
            ...session.messages,
            {
              id: chatMessage.id,
              type: 'multicast',
              attributes: {
                sender: chatMessage.sender,
                text: chatMessage.text,
                time: chatMessage.time,
              },
            },
          ],
          chat: {
            ...session.chat,
            messageLastReadId: chatMessage.id,
            participants,
          },
        },
      };
    },
    onMessageSeen(state, payload) {
      const { id: chatId, participantId, lastSeenMessageTimestamp } = payload;
      const session = state[chatId];
      if (!session) {
        return state;
      }

      return {
        ...state,
        [chatId]: {
          ...session,
          chat: {
            ...session.chat,
            participants: session.chat.participants.map(p => {
              if (p.participantId === participantId) {
                return { ...p, lastSeenMessageTimestamp };
              }
              return p;
            }),
          },
        },
      };
    },
    onMessageSetImportant(state, payload) {
      const { sessionId, messageId, important } = payload;
      const session = state[sessionId];
      if (!session) {
        return state;
      }

      return {
        ...state,
        [sessionId]: {
          ...session,
          messages: session.messages.map(message =>
            message.id === messageId
              ? { ...message, attributes: { ...message.attributes, important } }
              : message,
          ),
        },
      };
    },
    onMessageDeleted(state, payload) {
      const { id, sessionId } = payload;
      const session = state[sessionId];

      if (!session) {
        return state;
      }

      return {
        ...state,
        [sessionId]: {
          ...session,
          messages: session.messages.map(m =>
            m.id === id ? { ...m, deletedAt: new Date().toISOString() } : m,
          ),
        },
      };
    },
    onMessagesDeleted(state, payload) {
      const { sessionId, messages } = payload;
      const session = state[sessionId];
      const remainingMessageIds = messages.map(message => message.id);

      return {
        ...state,
        [sessionId]: {
          ...session,
          messages: session.messages.filter(({ id }) => remainingMessageIds.includes(id)),
        },
      };
    },
    'sessions/onLastReadMessageIdChanged': (state, { id, sessionId }) => {
      const session = state[sessionId];

      if (!session) {
        return state;
      }

      return {
        ...state,
        [sessionId]: {
          ...session,
          chat: {
            ...session.chat,
            messageLastReadId: id,
          },
        },
      };
    },
    'sessions/onParticipantRemoved': (state, { participantId, sessionId, userId }) => {
      const session = state[sessionId];

      if (!session) {
        return state;
      }

      // if self is removed, user no longer has access to chat
      // thus has to be resetted
      if (session.participant.userId === userId) {
        const newState = _.cloneDeep(state);
        delete newState[sessionId];

        return newState;
      }

      return {
        ...state,
        [sessionId]: {
          ...session,
          chat: {
            ...session.chat,
            participants: session.chat.participants.filter(p => participantId !== p.participantId),
          },
        },
      };
    },
    'sessions/onParticipantsChanged': (state, { participants, sessionId }) => {
      const session = state[sessionId];

      if (!session) {
        return state;
      }

      return {
        ...state,
        [sessionId]: {
          ...session,
          chat: {
            ...session.chat,
            participants,
          },
        },
      };
    },
    'sessions/onChatRemoved': (state, { id }) => {
      const session = state[id];

      if (!session) {
        return state;
      }

      const newState = _.cloneDeep(state);

      delete newState[id];

      return newState;
    },
    'sessions/onAuthUpdate': (state, { title, sessionId }) => {
      const session = state[sessionId];

      if (!session) {
        return state;
      }

      return {
        ...state,
        [sessionId]: {
          ...session,
          chat: {
            ...session.chat,
            title,
          },
        },
      };
    },
  },
  messageListeners: {
    SESSION_MSG_CREATED({ payload }) {
      this.onMessageCreated(payload);
    },
    SESSION_MULTI_MSG_CREATED({ payload }) {
      this.onMulticastMessageCreated(payload);
    },
    SESSION_MSG_DELETED({ payload }) {
      this.onMessageDeleted(payload);
    },
    SESSIONS_MODIFIED({ payload, state }) {
      const chats = payload.filter(d =>
        [SESSION_TYPES.CHAT, SESSION_TYPES.PAM_CHAT].includes(d.type),
      );
      if (chats.length) {
        const newState = updateStateChat(state.sessionChats, chats, state.superuser.id);

        for (const chat of chats) {
          if (
            newState[chat.id].chat?.participants.length !== chat.participants.length ||
            newState[chat.id].chat?.participants.some(
              (participant, index) =>
                participant.firstName !== chat.participants[index]?.firstName ||
                participant.lastName !== chat.participants[index]?.lastName,
            )
          ) {
            // if participants have changed
            this.loadChat({ id: chat.id });
            this.loadMessages({ id: chat.id, participantId: chat.participantId, tan: chat.tan });
            // eslint-disable-next-line no-continue
            continue;
          }
        }
        return newState;
      }
      return state;
    },
    SESSION_LAST_SEEN_MSG({ payload }) {
      this.onMessageSeen(payload);
    },
  },
  effects: () => ({
    async loadChat({ id }, { oidcSession, superuser }) {
      this.onLoading({ id, type: 'loadChat' });
      const url = `/sessions/${id}`;
      const [request] = makeApiRequest({ url, method: 'get' }, oidcSession);
      const { error, data } = await request;

      if (!error) {
        this.onChatLoaded(data.data, superuser.id);
        this.onSuccess({ id, type: 'loadChat' });
        return;
      }

      Logger.error(error);
      this.onError({ id, type: 'loadChat', error });
    },

    async loadMessages({ id, participantId, tan }, { oidcSession }) {
      this.onLoading({ id, type: 'loadMessages' });
      const isSessionChat = participantId && tan;
      const url = calculateEndpointRoute(id, participantId, tan);
      const [request] = makeApiRequest({ url, method: 'get' }, oidcSession, {
        internal: !isSessionChat,
      });
      const { error, data } = await request;

      if (!error) {
        this.onMessagesLoaded({ id, messages: data.data });
        this.onSuccess({ id, type: 'loadMessages' });
        return;
      }

      Logger.error(error);
      this.onError({ id, type: 'loadMessages', error });
    },
    async send({ id, text, files, snippetFiles, participantId, tan }, { oidcSession }) {
      this.onLoading({ id, type: 'send' });
      const isSessionChat = participantId && tan;
      const url = calculateEndpointRoute(id, participantId, tan);
      const body = {
        data: {
          type: 'sessions-message',
          attributes: {
            text,
            files,
            snippetFiles,
          },
        },
      };
      const [request] = makeApiRequest({ url, method: 'post', data: body }, oidcSession, {
        internal: !isSessionChat,
      });
      const { error, status } = await request;

      if (!error) {
        this.onSuccess({ id, type: 'send' });
        retryCount = 0;
        return;
      }
      // retry mechanism if files were not yet uploaded completely
      if (status === 424 && retryCount < 6) {
        retryCount += 1;
        setTimeout(() => this.send({ id, text, files }), 500);
        return;
      }

      Logger.error(error);
      this.onError({ id, type: 'send', error });
    },
    async setMessageImportant({ id, messageId, important }, { oidcSession }) {
      this.onLoading({ type: 'setMessageImportant' });
      const url = `/sessions/${id}/messages/${messageId}/important`;
      const body = {
        data: {
          type: 'sessions-message',
          attributes: {
            important,
          },
        },
      };
      const [request] = makeApiRequest({ url, method: 'patch', data: body }, oidcSession);
      const { error } = await request;

      if (!error) {
        this.onMessageSetImportant({ sessionId: id, messageId, important });
        this.onSuccess({ id, type: 'setMessageImportant' });
        return;
      }

      Logger.error(error);
      this.onError({ id, type: 'setMessageImportant', error });
    },
    async deleteMessage({ id, messageId }, { oidcSession }) {
      this.onLoading({ type: 'deleteMessage' });
      const url = `/sessions/${id}/messages/${messageId}`;
      const [request] = makeApiRequest({ url, method: 'delete' }, oidcSession);
      const { error } = await request;

      if (!error) {
        this.onSuccess({ id, type: 'deleteMessage' });
        return;
      }

      Logger.error(error);
      this.onError({ id, type: 'deleteMessage', error });
    },
    async deleteMessages({ id }, { oidcSession }) {
      this.onLoading({ type: 'deleteMessages' });
      const url = `/sessions/${id}/messages`;
      const [request] = makeApiRequest({ url, method: 'delete' }, oidcSession);
      const { error, data } = await request;

      if (!error) {
        this.onSuccess({ id, type: 'deleteMessages' });
        this.onMessagesDeleted({ sessionId: id, messages: data.data });
        return;
      }

      Logger.error(error);
      this.onError({ id, type: 'deleteMessages', error });
    },
    async joinChat({ id, permanentJoinId }, { oidcSession }) {
      const url = `/sessions/${id}/join/${permanentJoinId}`;
      const [request] = makeApiRequest({ url, method: 'patch' }, oidcSession);
      const { error } = await request;
      if (!error) {
        return;
      }

      this.onError({ id, type: 'joinChat', error });
      Logger.error(error);
    },
    async sendMulticast(payload, { oidcSession }) {
      const url = '/sessions/multicast-message';
      const body = {
        data: {
          type: 'sessions-message',
          attributes: {
            ...payload,
          },
        },
      };

      const [request] = makeApiRequest({ url, method: 'post', data: body }, oidcSession);

      const { error, status } = await request;

      if (!error) {
        retryCount = 0;
        return;
      }

      if (status === 424 && retryCount < 6) {
        retryCount += 1;
        setTimeout(() => this.send(payload), 500);
        return;
      }

      Logger.error(error);
    },
  }),
};

export default SessionChats;
